/**
 * Incrementally parses Server-Sent Events from a fetch Response body.
 * Invokes onEvent for each complete SSE message (double-newline delimited).
 */
export async function readSseResponse(
  response: Response,
  onEvent: (eventName: string, data: string) => void,
): Promise<void> {
  const body = response.body;
  if (!body) {
    return;
  }

  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const emit = (part: string) => {
    if (!part.trim()) {
      return;
    }

    let eventName = 'message';
    const dataLines: Array<string> = [];

    for (const line of part.split(/\r?\n/)) {
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).replace(/^\s/, ''));
      }
    }

    onEvent(eventName, dataLines.join('\n'));
  };

  try {
    let streamDone = false;
    while (!streamDone) {
      const { done, value } = await reader.read();
      if (done) {
        streamDone = true;
        // Nothing delimits the last event of a stream that ends without a trailing blank line, so
        // whatever is still buffered here is a whole event and used to be dropped on the floor. On
        // this surface that lost the run's final event: a complete answer read as cut off, or the
        // client never learned the session id it needed to reopen the thread.
        //
        // decode() with no argument also flushes any partial multi-byte character the last chunk
        // ended mid-way through, which the streaming decode above deliberately holds back.
        buffer += decoder.decode();
        emit(buffer);
        buffer = '';
      } else {
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split(/\r?\n\r?\n/);
        buffer = parts.pop() ?? '';

        for (const part of parts) {
          emit(part);
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
